import {
    type ViewStyle,
    type TextStyle,
    StyleSheet
} from "react-native";
import {
    type FieldActionIconColorsConstantType,
    type FieldCustomColorConstantType,
    type FieldIconColorConstantType,
    type FieldSizeKeyConstantType,
    type FieldColorsConstantType,
    type FieldSizeConstantType,
    type FieldDynamicStyleType,
    type FieldActionIconColors,
    type FieldStatusColorKeys,
    type FieldIDsConstantType,
    type FieldPaddingRole,
    type FieldMeasures,
    type FieldColors,
    type FieldSize,
    type FieldType,
    type FieldIDs
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getControlMeasures
} from "../../helpers/theme/control";
import {
    getFieldFocusStyle
} from "../../helpers/theme/state";
import {
    getInputTextStyle
} from "../../helpers/theme/input";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import {
    webStyle
} from "../../utils";

export const FIELD_PADDINGS: Record<FieldSize, FieldPaddingRole> = {
    large: {
        group: "controlPaddings",
        key: "medium"
    },
    medium: {
        group: "controlPaddings",
        key: "small"
    },
    small: {
        key: "spacingSm",
        group: "spaces"
    }
};

export const FIELD_STATUS_COLOR_KEYS: Record<FieldType, FieldStatusColorKeys> = {
    success: {
        hintTextColor: "success",
        borderColor: "success",
        iconColor: "success"
    },
    warning: {
        hintTextColor: "warning",
        borderColor: "warning",
        iconColor: "warning"
    },
    danger: {
        hintTextColor: "danger",
        borderColor: "danger",
        iconColor: "danger"
    },
    info: {
        borderColor: "default",
        hintTextColor: "info",
        iconColor: "info"
    },
    neutral: {
        borderColor: "default",
        hintTextColor: "mid",
        iconColor: "default"
    }
};

export const getFieldSizeKey = ({
    isActionable,
    pointerType,
    size
}: FieldSizeKeyConstantType): FieldSize => {
    if(size) {
        return size;
    }

    return isActionable && pointerType === "coarse" ? "large" : "medium";
};

export const getFieldSize = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    touchTargets,
    pointerType,
    typography,
    iconSizes,
    spaces,
    size
}: FieldSizeConstantType): FieldMeasures => {
    const controlMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const paddingRole = FIELD_PADDINGS[size];

    const actionSize = Math.min(touchTargets.fine, controlMeasures.minHeight - spaces.spacingXs * 2);

    return {
        ...controlMeasures,
        paddingHorizontal: paddingRole.group === "spaces" ? spaces[paddingRole.key] : controlPaddings[paddingRole.key],
        actionHitSize: pointerType === "coarse" ? controlMeasures.minHeight : Math.max(actionSize, touchTargets.fine),
        lineHeight: typography[controlMeasures.valueVariant].lineHeight,
        actionSize
    };
};

export const getFieldIDs = ({
    inputID
}: FieldIDsConstantType): FieldIDs => {
    return {
        hintTextID: `${inputID}-hint`,
        labelID: `${inputID}-label`,
        inputID
    };
};

export const getFieldCustomColor = ({
    tokenColors = {},
    customColor,
    colors
}: FieldCustomColorConstantType): string => {
    const _customColor = customColor as keyof NCoreUIKit.ProjectColorPalette;

    return colors.project[_customColor] ?? tokenColors[customColor] ?? customColor;
};

export const getFieldColors = ({
    isCounterWarning = false,
    isCounterOver = false,
    customBorderColor,
    customTitleColor,
    surfaceColor,
    customColor,
    isDisabled,
    isReadOnly,
    colors,
    type
}: FieldColorsConstantType): FieldColors => {
    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            containerColor: colors.system.state.overlay.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral,
            hintTextIconColorKey: "disabled",
            hintTextIconColor: disabledColor,
            placeholderColor: disabledColor,
            hintTextColor: disabledColor,
            counterColor: disabledColor,
            labelColor: disabledColor,
            valueColor: disabledColor,
            iconColorKey: "disabled",
            iconColor: disabledColor
        };
    }

    const statusColorKeys = FIELD_STATUS_COLOR_KEYS[type];

    const currentColors: FieldColors = {
        containerColor: isReadOnly ? colors.content.container.mid : (surfaceColor === "mid" || surfaceColor === "subtle") ? colors.content.container.default : colors.system.state.overlay.fill.default,
        counterColor: isCounterOver ? colors.content.text.danger : isCounterWarning ? colors.content.text.warning : colors.content.text.low,
        borderColor: isReadOnly ? colors.content.border.subtle : colors.content.border[statusColorKeys.borderColor],
        hintTextIconColor: colors.content.text[statusColorKeys.hintTextColor],
        hintTextColor: colors.content.text[statusColorKeys.hintTextColor],
        hintTextIconColorKey: statusColorKeys.iconColor,
        placeholderColor: colors.content.text.low,
        labelColor: colors.content.text.high,
        valueColor: colors.content.text.high,
        iconColor: colors.content.icon.mid,
        iconColorKey: "mid"
    };

    if(customColor) {
        currentColors.containerColor = getFieldCustomColor({
            tokenColors: colors.content.container,
            customColor,
            colors
        });
    }

    if(customBorderColor) {
        currentColors.borderColor = getFieldCustomColor({
            tokenColors: colors.content.border,
            customColor: customBorderColor,
            colors
        });
    }

    if(customTitleColor) {
        currentColors.labelColor = getFieldCustomColor({
            customColor: customTitleColor,
            colors
        });
    }

    return currentColors;
};

export const getFieldIconColor = ({
    customIconColor,
    currentColors,
    isDisabled,
    colors
}: FieldIconColorConstantType): string => {
    if(isDisabled || !customIconColor) {
        return currentColors.iconColor;
    }

    return getFieldCustomColor({
        customColor: customIconColor,
        colors
    });
};

export const getFieldActionIconColors = ({
    customIconColor,
    currentColors,
    isHighlighted,
    isDisabled,
    colors
}: FieldActionIconColorsConstantType): FieldActionIconColors => {
    if(isDisabled) {
        return {
            iconColor: colors.system.state.content.disabled.neutral,
            iconColorKey: "disabled"
        };
    }

    if(isHighlighted) {
        return {
            iconColor: colors.content.icon.default,
            iconColorKey: "default"
        };
    }

    return {
        iconColorKey: currentColors.iconColorKey,
        iconColor: getFieldIconColor({
            customIconColor,
            currentColors,
            isDisabled,
            colors
        })
    };
};

const stylesheet = StyleSheet.create({
    actionContainer: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    actionSurface: {
        justifyContent: "center",
        alignItems: "center",
        outlineOffset: 0
    },
    hintText: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    disabledControl: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    icon: {
        justifyContent: "center",
        alignItems: "center"
    },
    multilineControl: {
        alignItems: "flex-start"
    },
    hintTextIcon: {
        justifyContent: "center"
    },
    container: {
        flexDirection: "column"
    },
    hiddenActionContainer: {
        pointerEvents: "none"
    },
    stretchedContainer: {
        alignSelf: "stretch",
        flexShrink: 1,
        width: "100%"
    },
    control: {
        flexDirection: "row",
        borderStyle: "solid",
        alignItems: "center",
        ...webStyle({
            cursor: "text"
        })
    },
    counter: {
        marginStart: "auto",
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    },
    hintTextContent: {
        flexShrink: 1
    }
});

export const useStyles = ({
    isMinWidthEnabled,
    spreadBehaviour,
    currentColors,
    shapeConfigs,
    currentSize,
    isMultiline,
    pointerType,
    shadowAlpha,
    elevations,
    isDisabled,
    isReadOnly,
    typography,
    isFocused,
    isHovered,
    maxHeight,
    minHeight,
    userStyle,
    radiuses,
    borders,
    colors,
    spaces,
    type
}: FieldDynamicStyleType) => {
    const styles = {
        input: {
            ...getInputTextStyle({
                textStyle: typography[currentSize.valueVariant],
                pointerType,
                typography
            }),
            color: currentColors.valueColor
        } as Mutable<TextStyle>,
        control: {
            paddingHorizontal: currentSize.paddingHorizontal,
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            minHeight: currentSize.minHeight,
            ...getCornerStyle({
                radius: radiuses.form,
                shapeConfigs
            }),
            borderWidth: borders.line,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        hintTextIcon: {
            height: typography.bodyMediumSize.lineHeight
        } as Mutable<ViewStyle>,
        icon: {
            minWidth: currentSize.iconSize,
            height: currentSize.lineHeight
        } as Mutable<ViewStyle>,
        container: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        hintText: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>
    };

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, getHugStyle({
            style: userStyle
        }));
    }

    if(spreadBehaviour === "stretch") {
        Object.assign(styles.container, stylesheet.stretchedContainer);
    }

    if(isMultiline) {
        const controlMinHeight = minHeight ?? currentSize.minHeight * 2;

        const verticalInset = (spaces.spacingSm + borders.line) * 2;

        Object.assign(styles.control, stylesheet.multilineControl);

        styles.control.paddingVertical = spaces.spacingSm;

        styles.control.minHeight = controlMinHeight;

        styles.input.minHeight = controlMinHeight - verticalInset;

        if(maxHeight !== undefined) {
            styles.input.maxHeight = maxHeight - verticalInset;
        }
    } else {
        delete styles.input.lineHeight;
    }

    if(!isDisabled && !isReadOnly) {
        Object.assign(styles.control, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    if(isHovered && !isFocused && !isDisabled && !isReadOnly && currentColors.borderColor === colors.content.border.default) {
        styles.control.borderColor = colors.content.border.hover;
    }

    if(!isDisabled && (isFocused || !isReadOnly)) {
        Object.assign(styles.control, getFieldFocusStyle({
            isError: type === "danger",
            isFocused,
            borders,
            colors
        }));
    }

    if(isDisabled) {
        Object.assign(styles.control, stylesheet.disabledControl);

        Object.assign(styles.input, stylesheet.disabledControl);
    }

    if(isMinWidthEnabled) {
        styles.control.minWidth = shapeConfigs.fieldMinWidth;
    }

    return styles;
};

export default stylesheet;
